/**
 * Направление последней навигации — для анимации входа экрана.
 *
 * История роутера не сообщает тип перехода, а в `history.state` лежит только
 * `key` записи. Поэтому направление восстанавливается по стеку ключей:
 * ключ, встреченный раньше текущей позиции, — это «назад», позже —
 * «вперёд» кнопкой браузера, новый ключ — обычный переход.
 *
 * Сверх направления здесь два уточнения для рамки экрана (layouts/ScreenFrame):
 *  - `overrideNextDirection('back')` — переход, который семантически «назад», но
 *    делается новой записью (возврат на родителя по deep link): без подсказки
 *    он выглядел бы как «вперёд»;
 *  - `markSkipNextEnter()` — уход уже проигран на прежнем экране (кнопкой или
 *    жестом), и новый встаёт на место без повторной анимации.
 */
export type NavigationDirection = 'none' | 'forward' | 'back'

let keys: string[] = []
let paths: string[] = []
let position = -1
let direction: NavigationDirection = 'none'
/** Ключ записи, с которой ушли последним переходом. */
let fromKey: string | null = null
let overrideNext: NavigationDirection | null = null
let skipNextEnter = false
let skipEnter = false
/**
 * Переходы по истории (назад/вперёд к уже виденной записи) не анимировать: их уже
 * анимировал браузер — Safari на iPhone во вкладке показывает свой свайп снимками
 * страниц, и вторая анимация поверх выглядела бы рывком (см. `browserOwnsBackGesture`).
 * Уход по кнопке «назад» приложения проигрывается до перехода и сюда не относится.
 */
let skipTraversalEnter = false

/**
 * Запись, с которой началась работа вкладки, ключа не имеет: роутер ставит
 * `state.key` только своим переходам. Возврат к ней системной «Назад» приходит
 * тем же `undefined` — поэтому она лежит в стеке под собственным именем.
 */
const INITIAL_KEY = '__initial'

/** Вызывается на каждое изменение location; `key` берётся из `location.state.key`. */
export function trackNavigation(key: string | undefined, pathname = ''): void {
  let next: NavigationDirection
  /** Переход к уже виденной записи — это история (жест или кнопка браузера), а не новый экран. */
  let traversal = false
  const leaving = keys[position] ?? null
  const id = key || INITIAL_KEY

  if (position < 0) {
    // Начальная загрузка: направления нет. Запись остаётся в стеке, чтобы
    // первый переход из неё считался «вперёд» с историей, а возврат — «назад».
    keys = [id]
    paths = [pathname]
    position = 0
    next = 'none'
  } else {
    const index = keys.indexOf(id)
    if (index === -1) {
      keys = [...keys.slice(0, position + 1), id]
      paths = [...paths.slice(0, position + 1), pathname]
      position = keys.length - 1
      next = 'forward'
    } else {
      next = index < position ? 'back' : index > position ? 'forward' : 'none'
      traversal = next !== 'none'
      position = index
      paths[index] = pathname
    }
  }

  // Подсказки действуют ровно на один настоящий переход (смену записи) и ждут его.
  if (next !== 'none') {
    if (overrideNext) next = overrideNext
    overrideNext = null
    skipEnter = skipNextEnter || (skipTraversalEnter && traversal)
    skipNextEnter = false
  } else {
    skipEnter = false
  }

  fromKey = next === 'none' ? fromKey : leaving
  direction = next
}

export function getNavigationDirection(): NavigationDirection {
  return direction
}

/** Ключ текущей записи истории (по стеку) — под ним снимается снимок экрана при уходе. */
export function getCurrentKey(): string | null {
  return keys[position] ?? null
}

/** Ключ записи, с которой пришли последним переходом, — её снимок лежит под входящим экраном. */
export function getNavigationFromKey(): string | null {
  return fromKey
}

/** Ключ предыдущей записи — то, что покажет `history.back()`. */
export function getPreviousKey(): string | null {
  return position > 0 ? (keys[position - 1] ?? null) : null
}

/** Путь предыдущей записи — чтобы решить, вести ли «назад» историей или явным переходом. */
export function getPreviousPath(): string | null {
  return position > 0 ? (paths[position - 1] ?? null) : null
}

export function overrideNextDirection(next: NavigationDirection): void {
  overrideNext = next
}

export function markSkipNextEnter(): void {
  skipNextEnter = true
}

/** Уход с прежнего экрана уже проигран — входящий экран не анимируется. */
export function shouldSkipEnter(): boolean {
  return skipEnter
}

/**
 * Есть ли в истории вкладки предыдущая запись приложения. По deep link
 * (push-уведомление, ссылка) её нет — `history.back()` ушёл бы из приложения
 * или не сработал вовсе, и «назад» должно вести на родительский экран.
 */
export function hasInAppHistory(): boolean {
  return position > 0
}

/** Браузер сам анимирует возврат по истории (Safari на iPhone во вкладке): не повторять его. */
export function setSkipTraversalEnter(skip: boolean): void {
  skipTraversalEnter = skip
}
